import { useState } from "react";

import { useTranslation } from "react-i18next";

import { useInfiniteQuery, useMutation } from "@tanstack/react-query";
import { PlusIcon } from "lucide-react";

import { hasPermission } from "simplydo/core/permissions";
import { asMutation, initUseQuery, queryKey } from "simplydo/core/query";
import { useCachedState } from "simplydo/react/hooks";
import { RichText } from "simplydo/react/richtext";
import { styled } from "simplydo/react/styled";
import { Button } from "simplydo/react/ui/button";
import {
  Checkbox,
  CheckboxContent,
  CheckboxHeader,
} from "simplydo/react/ui/checkbox";
import { Divider } from "simplydo/react/ui/divider";
import { Dropdown } from "simplydo/react/ui/dropdown";
import { H3, Subheader } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from "simplydo/react/ui/modal";
import {
  Placeholder,
  PlaceholderLine,
  PlaceholderParagraph,
} from "simplydo/react/ui/placeholder";

import api from "api";
import { useAppSelector } from "store";

import AchievementFeedItem from "./FeedItems/AchievementFeedItem";
import ChallengeFeedItem from "./FeedItems/ChallengeFeedItem";
import FollowedChallengeSubmittedIdeaFeedItem from "./FeedItems/FollowedChallengeSubmittedIdeaFeedItem";
import GroupActivityFeedPostFeedItem from "./FeedItems/GroupActivityFeedPostFeedItem";
import GroupDiscussionFeedItem from "./FeedItems/GroupDiscussionFeedItem";
import IdeaCommentFeedItem from "./FeedItems/IdeaCommentFeedItem";
import IdeaFeedItem from "./FeedItems/IdeaFeedItem";
import OrganisationActivityFeedPostFeedItem from "./FeedItems/OrganisationActivityFeedPostFeedItem";

const feedOptions = [
  { key: "idea", text: "$t(generic.ideas,capitalise)", value: "idea" },
  {
    key: "challenge",
    text: "$t(generic.challenges,capitalise)",
    value: "challenge",
  },
  {
    key: "achievement",
    text: "$t(generic.achievements,capitalise)",
    value: "achievement",
  },
  {
    key: "organisationActivityFeedPosts",
    text: "$t(generic.organisations,capitalise)",
    value: "organisationActivityFeedPosts",
  },
  { key: "group", text: "$t(generic.groups,capitalise)", value: "group" },
];
const allFeedOptions = feedOptions.map((option) => option.value);

const FeedItemsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  max-height: 100%;
  width: calc(100% + 12px);
  padding-right: 12px;
  overflow: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.2) transparent;

  &:hover {
    scrollbar-color: rgba(0, 0, 0, 0.35) transparent;
  }

  > div {
    position: relative;

    &:not(:last-child):before {
      content: "";
      position: absolute;
      bottom: -1rem;
      left: 0;
      width: 100%;
      height: 1px;
      border-bottom: 1px solid ${({ theme }) => theme.colors.primary.softer};
    }
  }
`;

const ActivityFeedBlock = () => {
  const { t } = useTranslation();
  const user = useAppSelector((state) => state.user);

  const [include, setInclude] = useState<string>("all");

  const [postEditor, setPostEditor] = useState({
    isOpen: false,
    isPinned: false,
    isAnonymous: false,
  });

  const [message, setMessage] = useCachedState<string>(
    user?._id && user.organisation
      ? {
          kind: "orgFeedPost",
          forType: "organisation",
          forId: user.organisation,
          user: user._id,
        }
      : undefined,
    "",
  );

  const createPost = useMutation({
    mutationFn: asMutation(api.organisations.postToActivityFeed)!,
    onSuccess: (data, variables) => {
      setPostEditor({ isOpen: false, isPinned: false, isAnonymous: false });
      setMessage("");
      api.setInfiniteQueryData(api.users.feed, [], (prev) => ({
        ...prev,
        pages: prev.pages.map((page, index) => ({
          ...page,
          feed:
            index === 0
              ? [
                  {
                    ...data.post,
                    type: "organisationActivityFeedPost",
                    owner: variables[1].isAnonymous ? undefined : user!,
                  },
                  ...page.feed,
                ]
              : page.feed,
        })),
      }));
    },
  });

  const feedQuery = useInfiniteQuery({
    queryKey: queryKey(api.users.feed, [
      {
        include: include === "all" ? allFeedOptions : [include],
        page: 1,
        limit: 10,
      },
    ]),
    queryFn: ({ pageParam }) =>
      initUseQuery(api.users.feed, [
        {
          include: include === "all" ? allFeedOptions : [include],
          page: pageParam,
          limit: 10,
        },
      ]).queryFn(),
    initialPageParam: 1,
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      return lastPage.canExtend ? lastPageParam + 1 : undefined;
    },
  });

  if (feedQuery.isLoading)
    return (
      <Placeholder>
        <PlaceholderParagraph>
          <PlaceholderLine style={{ height: 20 }} length="medium" />
          <PlaceholderLine style={{ height: 20 }} />
          <PlaceholderLine style={{ height: "60vh" }} />
        </PlaceholderParagraph>
      </Placeholder>
    );

  return (
    <div
      style={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <div>
          <H3>
            Activity feed
            <Subheader>The latest updates for you.</Subheader>
          </H3>
        </div>
        <Dropdown
          value={include || "all"}
          style={{ marginTop: 2 }}
          onChange={(e, { value }) => setInclude(value as string)}
          options={[
            { key: "all", text: "All", value: "all" },
            ...feedOptions.map((option) => ({
              ...option,
              text: t(option.text),
            })),
          ]}
        />
      </div>
      {hasPermission(user, "org.viewDashboard", user!.organisation) ? (
        <div>
          <Button
            size="small"
            color="primary"
            variant="soft"
            fluid
            onClick={() => setPostEditor((prev) => ({ ...prev, isOpen: true }))}
          >
            <Icon icon={PlusIcon} /> New post
          </Button>
        </div>
      ) : null}
      <Modal
        open={postEditor.isOpen}
        onClose={() => setPostEditor((prev) => ({ ...prev, isOpen: false }))}
      >
        <ModalHeader>New activity feed post</ModalHeader>
        <ModalContent>
          <RichText
            preset="simple"
            value={message}
            onChange={(value) => setMessage(value)}
          />
          <Divider hidden />
          <p>
            These posts will show to users of this organisation in their
            activity feed. By default, they will appear at the top of the feeds
            for 7 days, after which they will filter into the timeline.
          </p>

          <Checkbox
            checked={postEditor.isPinned}
            onChange={() =>
              setPostEditor((prev) => ({ ...prev, isPinned: !prev.isPinned }))
            }
          >
            <CheckboxHeader>Pin to top</CheckboxHeader>
            <CheckboxContent>
              Toggle this on to pin the post to the top of all users&apos;
              activity feeds. If multiple posts are pinned, they will be ordered
              by recency.
            </CheckboxContent>
          </Checkbox>
          <Checkbox
            checked={postEditor.isAnonymous}
            onChange={() =>
              setPostEditor((prev) => ({
                ...prev,
                isAnonymous: !prev.isAnonymous,
              }))
            }
          >
            <CheckboxHeader>Post anonymously</CheckboxHeader>
            <CheckboxContent>
              Toggle this on to post anonymously. The organisation name will be
              shown as the author.
            </CheckboxContent>
          </Checkbox>
        </ModalContent>
        <ModalActions>
          <Button
            onClick={() =>
              setPostEditor((prev) => ({ ...prev, isOpen: false }))
            }
          >
            Cancel
          </Button>
          <Button
            color="primary"
            disabled={!message}
            loading={createPost.isPending}
            onClick={() => {
              createPost.mutate([
                user!.organisation,
                {
                  message,
                  isPinned: postEditor.isPinned,
                  isAnonymous: postEditor.isAnonymous,
                },
              ]);
            }}
          >
            Post
          </Button>
        </ModalActions>
      </Modal>
      <FeedItemsContainer>
        {feedQuery.data?.pages
          .flatMap((page) => page.feed)
          .map((item) => {
            const key = `${item.type}-${item._id}`;
            switch (item.type) {
              case "groupFeedComment":
                return <GroupDiscussionFeedItem key={key} item={item} />;
              case "idea":
                return <IdeaFeedItem key={key} item={item} />;
              case "challenge":
              case "discoverChallenge":
                return <ChallengeFeedItem key={key} item={item} />;
              case "followedChallengeSubmittedIdea":
                return (
                  <FollowedChallengeSubmittedIdeaFeedItem
                    key={key}
                    item={item}
                  />
                );
              case "ideaComment":
                return <IdeaCommentFeedItem key={key} item={item} />;
              case "achievement":
                return <AchievementFeedItem key={key} item={item} />;
              case "organisationActivityFeedPost":
                return (
                  <OrganisationActivityFeedPostFeedItem key={key} item={item} />
                );
              case "groupActivityFeedPost":
                return <GroupActivityFeedPostFeedItem key={key} item={item} />;
              default:
                return null;
            }
          })}
        {feedQuery.hasNextPage ? (
          <div style={{ minHeight: 1 }}>
            <Button
              fluid
              size="small"
              color="primary"
              variant="ghost"
              onClick={() => feedQuery.fetchNextPage()}
              disabled={!feedQuery.hasNextPage}
              loading={feedQuery.isFetchingNextPage}
            >
              Load more
            </Button>
          </div>
        ) : null}
      </FeedItemsContainer>
    </div>
  );
};

export default ActivityFeedBlock;
